import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useState } from "react";

import { BarChart01Icon } from "@/components/Icons/BarChart01Icon";
import { CalendarHeart02Icon } from "@/components/Icons/CalendarHeart02Icon";
import { CalendarIcon } from "@/components/Icons/CalendarIcon";
import { DotsGridIcon } from "@/components/Icons/DotsGridIcon";
import { File02Icon } from "@/components/Icons/File02Icon";
import { Home02Icon } from "@/components/Icons/Home02Icon";
import { Inbox01Icon } from "@/components/Icons/Inbox01Icon";
import { MessageChatCircleIcon } from "@/components/Icons/MessageChatCircleIcon";
import { PieChart01Icon } from "@/components/Icons/PieChart01Icon";
import { SearchRefractionIcon } from "@/components/Icons/SearchRefractionIcon";
import { Settings01Icon } from "@/components/Icons/Settings01Icon";
import { Stars02Icon } from "@/components/Icons/Stars02Icon";
import { Tool02Icon } from "@/components/Icons/Tool02Icon";
import type { SidebarBaseItemType, SidebarItemType } from "@/components/Sidebar/types";
import {
  DEFAULT_COMMUNITY_FEED_TAB_ADMIN,
  DEFAULT_COMMUNITY_FEED_TAB_RESIDENT,
  type MessageRouteParams,
} from "@/helpers/community-feed";
import { hasAdminProject } from "@/helpers/portfolio-access";
import { SIGNALR_EVENTS } from "@/helpers/signalr";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUserOptional } from "@/hooks/Network/useSessionUser";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { usePermission } from "@/hooks/usePermission";
import { useSignalRHub, useSignalRSubscription } from "@/hooks/useSignalR";
import { useSlug } from "@/hooks/useSlug";
import { useConfig } from "@/providers/config/useConfig";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import {
  canAccessAddresses,
  canAccessAdminTickets,
  canAccessAnalytics,
  canAccessAssets,
  canAccessBuildings,
  canAccessCalendar,
  canAccessChats,
  canAccessCommunityFeed,
  canAccessCompanies,
  canAccessHelpCategories,
  canAccessHome,
  canAccessInterestGroups,
  canAccessNeighbours,
  canAccessPracticalAndMore,
  canAccessProjectIntegrationSettings,
  canAccessProjectOnboadingScreens,
  canAccessProjectPlusButtonOptions,
  canAccessProjectRolesAndPermissions,
  canAccessProjectSettings,
  canAccessProjectStyling,
  canAccessRegularSurveys,
  canAccessResidentTickets,
  canAccessServices,
} from "./permissions";

export const useProjectSidebarManager = (): {
  navigationItems: SidebarItemType[];
  canViewPortfolio: boolean;
} => {
  const [amountUnreadMessages, setAmountUnreadMessages] = useState<{
    [projectId: string]: number;
  }>({});

  const projectId = useProjectId();
  const isDesktop = useIsDesktop();
  const connectedProjects = useLinkedProjects();
  const hasPermission = usePermission();
  const slug = useSlug();
  const postHog = usePostHog();
  // The legacy config name is broader than its current purpose: it controls only whether
  // Explore more is discoverable in navigation, not standard navigation or route availability.
  const isExploreMoreNavigationAvailable = useConfig("isAnalyticsInsightsAvailable");
  const isCommunityFeedSentimentAvailable = useConfig("isCommunityFeedSentimentAvailable");
  const [sessionUser] = useSessionUserOptional({
    keepPreviousData: true,
  });
  const { t } = useTranslation();
  const onNewChatMessage = useCallback(
    (
      ...args: [
        {
          unreadChats: number;
          fromConnectionStarted: boolean;
        },
      ]
    ) => {
      if (args[0].unreadChats > 0) {
        setAmountUnreadMessages((x) => ({
          ...x,
          [projectId]: args[0].unreadChats,
        }));
      } else {
        setAmountUnreadMessages((x) => ({
          ...x,
          [projectId]: 0,
        }));
      }
    },
    [projectId],
  );
  const { signalRConnection } = useSignalRHub("notification-hub", {
    query: `userId=${sessionUser?.id}`,
    disabled: !sessionUser,
  });
  useSignalRSubscription(signalRConnection, SIGNALR_EVENTS.CHAT.UPDATE_PROJECT_CHAT_UNREAD_COUNT, onNewChatMessage);

  const project = sessionUser?.project;
  const canViewPortfolio = useMemo(() => hasAdminProject(connectedProjects.data), [connectedProjects.data]);
  const items: SidebarItemType[] = [];

  if (!isDesktop && canViewPortfolio) {
    items.push({
      key: "portfolio",
      label: t("navigation.portfolio.portfolio"),
      to: routes.portfolio.overview(),
      icon: <DotsGridIcon />,
      "data-testid": "navigation-project-portfolio",
      exact: true,
    });
  }

  if (hasPermission(canAccessHome)) {
    items.push({
      key: "home",
      label: t("navigation.home"),
      to: routes.home.home({
        slug,
      }),
      icon: <Home02Icon />,
      "data-testid": "navigation-home",
      exact: true,
    });
  }

  if (hasPermission(canAccessAnalytics)) {
    const analyticsItems: SidebarBaseItemType[] = [
      {
        key: "analytics-community-feed",
        label: t("page.analytics-community-feed.breadcrumb"),
        to: routes.analytics.communityFeedReporting({ slug }),
        exact: true,
        "data-testid": "navigation-analytics-community-feed",
      },
    ];

    if (isCommunityFeedSentimentAvailable) {
      analyticsItems.push({
        key: "analytics-sentiment",
        label: t("page.analytics-community-feed.sentiment.breadcrumb"),
        to: routes.analytics.communityFeedSentimentReporting({ slug }),
        "data-testid": "navigation-analytics-sentiment",
      });
    }

    analyticsItems.push(
      {
        key: "analytics-tickets",
        label: t("page.analytics-tickets.breadcrumb"),
        to: routes.analytics.ticketReporting({ slug }),
        "data-testid": "navigation-analytics-tickets",
      },
      {
        key: "analytics-events",
        label: t("page.analytics-events.breadcrumb"),
        to: routes.analytics.eventReporting({ slug }),
        "data-testid": "navigation-analytics-events",
      },
      {
        key: "analytics-bookings",
        label: t("navigation.portfolio.analytics-bookings"),
        to: routes.analytics.bookingReporting({ slug }),
        "data-testid": "navigation-analytics-bookings",
      },
    );

    if (isExploreMoreNavigationAvailable) {
      analyticsItems.push({
        key: "analytics-explore",
        label: t("page.analytics-navigation.explore-all"),
        to: routes.analytics.explore({ slug }),
        "data-testid": "navigation-analytics-explore",
      });
    }

    items.push({
      key: "analytics-group",
      label: t("navigation.analytics"),
      to: routes.analytics.overview({ slug }),
      icon: <PieChart01Icon />,
      "data-testid": "navigation-analytics",
      type: "group",
      items: analyticsItems,
    });
  }

  if (
    hasPermission(canAccessNeighbours) ||
    hasPermission(canAccessAddresses) ||
    hasPermission(canAccessCompanies) ||
    hasPermission(canAccessBuildings) ||
    hasPermission(canAccessInterestGroups) ||
    hasPermission(canAccessHelpCategories)
  ) {
    const subItemsDiscover: SidebarBaseItemType[] = [];
    if (hasPermission(canAccessNeighbours)) {
      subItemsDiscover.push({
        key: "neighbours",
        label: t("navigation.discover.neighbours"),
        to: routes.users.list({
          slug,
        }),
        onClick: () => postHog.capture("clicked_all_neighbours"),
        "data-testid": "navigation-neighbours",
      });
    }

    if (hasPermission(canAccessAddresses)) {
      subItemsDiscover.push({
        key: "addresses",
        label: t("navigation.discover.addresses"),
        to: routes.addresses.list({
          slug,
        }),
        "data-testid": "navigation-addresses",
      });
    }

    if (hasPermission(canAccessCompanies) && project?.type === "companyBased") {
      subItemsDiscover.push({
        key: "companies",
        label: t("navigation.discover.companies"),
        to: routes.companies.list({
          slug,
        }),
        "data-testid": "navigation-companies",
      });
    }

    if (hasPermission(canAccessBuildings)) {
      subItemsDiscover.push({
        key: "buildings",
        label: t("navigation.discover.buildings"),
        to: routes.buildings.list({
          slug,
        }),
        "data-testid": "navigation-buildings",
      });
    }

    if (hasPermission(canAccessInterestGroups)) {
      subItemsDiscover.push({
        key: "interest-groups",
        label: t("navigation.discover.groups"),
        to: routes.interestGroups.list({
          slug,
        }),
        "data-testid": "navigation-link-interest-groups",
      });
    }

    if (hasPermission(canAccessHelpCategories)) {
      subItemsDiscover.push({
        key: "help-categories",
        label: t("navigation.discover.help"),
        to: routes.helpCategories.list({
          slug,
        }),
        "data-testid": "navigation-link-help-categories",
      });
    }
    items.push({
      key: "discover-group",
      label: t("navigation.discover"),
      icon: <SearchRefractionIcon />,
      "data-testid": "navigation-discovery-group",
      type: "group",
      items: subItemsDiscover,
    });
  }

  if (hasPermission(canAccessCommunityFeed)) {
    const params: MessageRouteParams["list"] = {
      tab: sessionUser?.isAdmin ? DEFAULT_COMMUNITY_FEED_TAB_ADMIN : DEFAULT_COMMUNITY_FEED_TAB_RESIDENT,
    };

    items.push({
      key: "community-feed",
      label: t("navigation.community-feed"),
      to:
        routes.communityFeed.list({
          slug,
        }) + `?${new URLSearchParams(params).toString()}`,
      icon: <File02Icon />,
      "data-testid": "navigation-community-feed",
      onClick: () => postHog.capture("clicked_community_feed"),
    });
  }

  if (hasPermission(canAccessChats)) {
    items.push({
      key: "chats",
      label: t("navigation.chats"),
      to: routes.chats.list({
        slug,
      }),
      icon: <MessageChatCircleIcon />,
      "data-testid": "navigation-chats",
      isNewEventAvailable: amountUnreadMessages[projectId] > 0,
      newEventCount: amountUnreadMessages[projectId],
    });
  }

  if (hasPermission(canAccessCalendar) && sessionUser?.calendarVisibility !== "none") {
    items.push({
      key: "calendar",
      label: t("navigation.calendar"),
      to: routes.calendar.list({
        slug,
      }),
      icon: <CalendarIcon />,
      "data-testid": "navigation-calendar",
      onClick: () => postHog.capture("clicked_calendar"),
    });
  }

  if (hasPermission(canAccessServices)) {
    items.push({
      key: "services",
      label: t("navigation.services"),
      to: routes.servicePartners.list({
        slug,
      }),
      icon: <Stars02Icon />,
      "data-testid": "navigation-service-list",
    });
  }

  if (hasPermission(canAccessAssets)) {
    items.push({
      key: "assets",
      label: t("navigation.bookings.assets"),
      to: routes.bookings.list({
        slug,
      }),
      icon: <CalendarHeart02Icon />,
      "data-testid": "navigation-assets",
    });
  }

  if (hasPermission(canAccessResidentTickets)) {
    items.push({
      key: "resident-tickets",
      label: t("navigation.resident-tickets"),
      to: routes.tickets.list({
        slug,
      }),
      icon: <Tool02Icon />,
      "data-testid": "navigation-resident-tickets",
    });
  } else if (hasPermission(canAccessAdminTickets)) {
    items.push({
      key: "tickets",
      label: t("navigation.tickets"),
      to: routes.tickets.list({
        slug,
      }),
      icon: <Tool02Icon />,
      "data-testid": "navigation-tickets",
      onClick: () => postHog.capture("clicked_all_tickets"),
    });
  }

  if (hasPermission(canAccessPracticalAndMore)) {
    items.push({
      key: "documents",
      label: t("navigation.documents"),
      to: routes.documents.rootList({
        slug,
      }),
      icon: <Inbox01Icon />,
      "data-testid": "navigation-documents-root-list",
    });
  }

  if (hasPermission(canAccessRegularSurveys)) {
    items.push({
      key: "surveys",
      label: t("navigation.surveys"),
      icon: <BarChart01Icon />,
      to: routes.surveys.list({
        slug,
      }),
      "data-testid": "navigation-surveys",
    });
  }

  if (
    hasPermission(canAccessProjectSettings) ||
    hasPermission(canAccessProjectStyling) ||
    hasPermission(canAccessProjectOnboadingScreens) ||
    hasPermission(canAccessProjectRolesAndPermissions) ||
    hasPermission(canAccessProjectPlusButtonOptions) ||
    hasPermission(canAccessProjectIntegrationSettings)
  ) {
    const subItemsAdmin: SidebarBaseItemType[] = [];

    if (hasPermission(canAccessProjectSettings)) {
      subItemsAdmin.push({
        key: "project-settings",
        label: t("navigation.admin.settings"),
        to: routes.projects.edit({
          slug,
        }),
        "data-testid": "navigation-project-settings",
      });
    }
    if (hasPermission(canAccessProjectStyling)) {
      subItemsAdmin.push({
        key: "project-styling",
        label: t("navigation.admin.styling"),
        to: routes.projects.styling({
          slug,
        }),
        "data-testid": "navigation-project-styling",
      });
    }
    if (hasPermission(canAccessProjectOnboadingScreens)) {
      subItemsAdmin.push({
        key: "onboarding-screens",
        label: t("navigation.admin.onboarding"),
        to: routes.onboardingScreens.list({
          slug,
        }),
        "data-testid": "navigation-onboarding-screens",
      });
    }
    if (hasPermission(canAccessProjectRolesAndPermissions)) {
      subItemsAdmin.push({
        key: "roles",
        label: t("navigation.admin.roles"),
        to: routes.roles.list({
          slug,
        }),
        "data-testid": "navigation-role-list",
      });
    }
    if (hasPermission(canAccessProjectPlusButtonOptions)) {
      subItemsAdmin.push({
        key: "plus-button",
        label: t("navigation.admin.plus-button"),
        to: routes.projects.plusButtonOptions({
          slug,
        }),
        "data-testid": "navigation-plus-button-options",
      });
    }
    if (hasPermission(canAccessProjectIntegrationSettings)) {
      subItemsAdmin.push({
        key: "integrations",
        label: t("navigation.admin.integrations"),
        to: routes.projects.integrations({
          slug,
        }),
        "data-testid": "navigation-integration-settings",
      });
    }
    items.push({
      key: "admin-group",
      label: t("navigation.admin"),
      icon: <Settings01Icon />,
      "data-testid": "navigation-management-group",
      type: "group",
      items: subItemsAdmin,
    });
  }

  return {
    navigationItems: items,
    canViewPortfolio,
  };
};
